/**
 * Sandbox Status Badge
 *
 * Displays a status badge for sandbox templates, with a simple spinner for active states.
 *
 * @module features/sandboxes/components/SandboxStatusBadge
 */

import React from 'react';
import { TagBadge, type TagColor } from '@chatsdet/ui';
import { CircleNotch } from '@phosphor-icons/react';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';

type SandboxStatusBadgeProps = {
  status: EnvironmentTemplate['status'];
  /** Prefix label rendered before the status (e.g. "Template"). */
  prefix?: string;
  size?: 'sm' | 'md' | 'lg';
};

const STATUS_CONFIG: Record<
  EnvironmentTemplate['status'],
  { label: string; color: TagColor; showSpinner: boolean }
> = {
  verified: { label: 'Ready', color: 'green', showSpinner: false },
  provisioning: { label: 'Provisioning', color: 'blue', showSpinner: true },
  sandbox_ready: { label: 'Sandbox Ready', color: 'gray', showSpinner: false },
  authoring: { label: 'Setting Up', color: 'blue', showSpinner: true },
  verifying: { label: 'Verifying', color: 'blue', showSpinner: true },
  failed: { label: 'Failed', color: 'red', showSpinner: false },
  draft: { label: 'Draft', color: 'gray', showSpinner: false },
};

export const SandboxStatusBadge: React.FC<SandboxStatusBadgeProps> = ({ status, prefix, size = 'md' }) => {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  const label = prefix ? `${prefix} ${config.label}` : config.label;

  return (
    <TagBadge
      name={label}
      color={config.color}
      size={size}
      icon={config.showSpinner ? <CircleNotch size={12} className="animate-spin" /> : undefined}
      hideDot={!config.showSpinner}
    />
  );
};
